const api_url = process.env.REACT_APP_BACKEND_URL;

export type Card = {
  id: number,
  multiverse_id: number,
  original_name: string,
  ja_name: string,
  name_furigana: string,
  mana_cost: string,
  cmc: number,
  type_line: string,
  supertypes: string[],
  types: string[],
  subtypes: string[],
  ja_type_line: string,
  ja_supertypes: string[],
  ja_types: string[],
  ja_subtypes: string[],
  oracle_text: string,
  ja_text: string,
  power: string,
  toughness: string,
  colors: string[],
  color_identity: string[],
  layout: string,
  card_faces: CardFaces | undefined,
  printings: string[],
  first_printing: string,
  first_modern_printing: string,
  modern_legal: string,
  image_url: string,
  image_url_small: string,
  image_url_large: string,
};

export type CardFaces = {
  face1: CardFace,
  face2: CardFace,
};

export type CardFace = {
  original_name: string,
  ja_name: string,
  name_furigana: string,
  mana_cost: string,
  type_line: string,
  ja_type_line: string,
  oracle_text: string,
  ja_text: string,
};

export type CardName = {
  original_name: string,
  ja_name: string,
  name_furigana: string,
};

export type FuzzySearchResult = {
  card_names: CardName[],
  language: string,
};

export type CardResult = {
  count: number,
  cards: Card[],
};

export type CardType = {
  ID: number,
  en: string,
  ja: string,
  furigana: string,
  category: string,
  sub_category: string,
};

export type CardFilter = {
  name_or_text: string,
  name_search: boolean,
  w: boolean,
  u: boolean,
  b: boolean,
  r: boolean,
  g: boolean,
  colorless: boolean,
  multicolored: boolean,
  colorIdentity: boolean,
  cost0: boolean,
  cost1: boolean,
  cost2: boolean,
  cost3: boolean,
  cost4: boolean,
  cost5: boolean,
  cost6: boolean,
  cost7: boolean,
  cost8: boolean,
  cost9: boolean,
  costOver10: boolean,
  supertypes: CardType[],
  types: CardType[],
  subtypes: CardType[],
  powerFr: string,
  powerTo: string,
  toughnessFr: string,
  toughnessTo: string,
  order: Order,
  orderASC: boolean,
  pageSize: number,
  page: number,
};

export type Order = {
  key: string,
  query: string,
};

export const statusRange = [
  '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'
];
export const orderKeys: Order[] = [
  { key: '名前（日本語）', query: 'name_furigana' },
  { key: '名前（英語）', query: 'original_name' },
  { key: 'マナ総量', query: 'cmc' },
  { key: 'パワー', query: 'power' },
  { key: 'タフネス', query: 'toughness' }
]

export const fetchCardTypes = async (cardType: string) => {
  const endpoint = `${api_url}/cardtypes?category=${cardType}`;

  const data = await fetch(endpoint).then((response) => response.json());
  return data;
};

// return CardResult
export const fetchCards = async (cardFilter: CardFilter) => {
  const queryParams = generateQueryParams(cardFilter)
  const endpoint = `${api_url}/cards${queryParams}`;

  const data = await fetch(endpoint).then((response) => response.json());
  return data;
};

export const fetchCardNames = async (name: string) => {
  const endpoint = `${api_url}/cards/${name}`;
  const data = await fetch(endpoint).then((response) => response.json());
  return data;
};

const generateQueryParams = (cardFilter: CardFilter): string => {
  var queries: string[] = [];
  queries = queries.concat(
    generateNameQuery(cardFilter),
    generateColorsQuery(cardFilter),
    generateManaCostsQuery(cardFilter),
    generateTypesQuery(cardFilter),
    generatePowerToughnessQuery(cardFilter),
    generateOrderQuery(cardFilter),
    generatePageQuery(cardFilter)
  );

  if (queries.length === 0) {
    return '';
  } else {
    const queryParams = '?' + queries.join('&');
    return queryParams;
  }
}

const generateNameQuery = (cardFilter: CardFilter): string[] => {
  return [
    cardFilter.name_search
      ? 'name=' + encodeURIComponent(cardFilter.name_or_text)
      : 'text=' + encodeURIComponent(cardFilter.name_or_text)
  ];
}

const generateColorsQuery = (cardFilter: CardFilter): string[] => {
  const keywords = cardFilter.colorIdentity
    ? ['W', 'U', 'B', 'R', 'G']
    : ['White', 'Blue', 'Black', 'Red', 'Green']
    ;
  const colors: boolean[] = [cardFilter.w, cardFilter.u, cardFilter.b, cardFilter.r, cardFilter.g];

  var colorQuery: string[] = keywords.filter(
    (colorName, idx) => colors[idx] === true
  ).map(
    (colorName) => 'colors=' + colorName
  );
  if (cardFilter.multicolored === true) {
    colorQuery.push('multicolored=1');
  }
  if (cardFilter.colorless === true) {
    colorQuery.push('colorless=1');
  }
  if (cardFilter.colorIdentity === true) {
    colorQuery.push('coloridentity=1');
  }

  return colorQuery;
};

const generateManaCostsQuery = (cardFilter: CardFilter): string[] => {
  const manaCosts: boolean[] = [
    cardFilter.cost0,
    cardFilter.cost1,
    cardFilter.cost2,
    cardFilter.cost3,
    cardFilter.cost4,
    cardFilter.cost5,
    cardFilter.cost6,
    cardFilter.cost7,
    cardFilter.cost8,
    cardFilter.cost9,
  ];
  const qString = 'cmc=';
  var manaCostQuery: string[] = manaCosts.flatMap((b, idx) =>
    b === true
      ? `${qString}${idx}`
      : []
  );
  if (cardFilter.costOver10 === true) {
    manaCostQuery.push('cmcover=10');
  }

  return manaCostQuery;
};

const generateTypesQuery = (cardFilter: CardFilter): string[] => {
  const supertypesQuery = cardFilter.supertypes.map(s => `supertypes=${s.en}`)
  const typesQuery = cardFilter.types.map(s => `types=${s.en}`)
  const subtypesQuery = cardFilter.subtypes.map(s => `subtypes=${s.en}`)

  return supertypesQuery.concat(typesQuery, subtypesQuery);
};

const generatePowerToughnessQuery = (cardFilter: CardFilter): string[] => {
  var q = [];
  if (cardFilter.powerFr !== statusRange[0]) {
    q.push(`powerfr=${cardFilter.powerFr}`);
  }
  if (cardFilter.powerTo !== statusRange[0]) {
    q.push(`powerto=${cardFilter.powerTo}`);
  }
  if (cardFilter.toughnessFr !== statusRange[0]) {
    q.push(`toughnessfr=${cardFilter.toughnessFr}`);
  }
  if (cardFilter.toughnessTo !== statusRange[0]) {
    q.push(`toughnessto=${cardFilter.toughnessTo}`);
  }
  return q;
};

const generateOrderQuery = (cardFilter: CardFilter): string[] => {
  return [
    `orderby=${cardFilter.order.query}`,
    `orderdir=${cardFilter.orderASC ? 'asc' : 'desc'}`
  ];
};

const generatePageQuery = (cardFilter: CardFilter): string[] => {
  return [
    `pagesize=${cardFilter.pageSize}`,
    `page=${cardFilter.page}`
  ];
};
